/**
 * Transformers for converting backend quiz data to frontend shape.
 */
import { QuizAIService } from '../../utils/quiz-ai-service';
import { decodeQuizHtmlEntities, resolveMultipleChoiceCorrectIndex } from '../../utils/quiz-text-utils';
import { parseFigureSpec } from '../../figures/schemas';
import { skillTagsFromQuestion } from '../../utils/skill-key';
const quizAIService = QuizAIService.getInstance();
const VALID_QUIZ_STATUSES = ['draft', 'published', 'archived'];
function toQuizStatus(s) {
    if (s && VALID_QUIZ_STATUSES.includes(s))
        return s;
    return 'draft';
}
/**
 * Keys whose values are identifiers/enums, never prose. Skipped so the decoder
 * can never alter an id or a status token.
 */
const NON_TEXT_QUESTION_KEYS = new Set([
    'id',
    'leftId',
    'rightId',
    'type',
    'difficulty',
    'points',
    'position',
    'source',
    'generatedAt',
    'skill_codes',
]);
/**
 * Recursively decode HTML entities in stored quiz content.
 *
 * The API's input sanitizer HTML-encodes every string it receives, so text
 * arrives as `6&#x2F;x` (and worse for rows written before encoding was made
 * idempotent). Decoding here — at the single read boundary — means the editor,
 * the preview and every downstream consumer all see the tutor's real text.
 */
const decodeContentDeep = (value) => {
    if (typeof value === 'string') {
        return decodeQuizHtmlEntities(value);
    }
    if (Array.isArray(value)) {
        return value.map((item) => decodeContentDeep(item));
    }
    if (value && typeof value === 'object') {
        const out = {};
        for (const [key, val] of Object.entries(value)) {
            out[key] = NON_TEXT_QUESTION_KEYS.has(key) ? val : decodeContentDeep(val);
        }
        return out;
    }
    return value;
};
/** Map backend difficulty strings to legacy format */
export const mapBackendDifficultyToLegacy = (difficulty) => {
    const map = {
        beginner: 'easy',
        intermediate: 'medium',
        advanced: 'hard',
        easy: 'easy',
        medium: 'medium',
        hard: 'hard',
    };
    if (!difficulty)
        return 'medium';
    return map[difficulty.toLowerCase()] || 'medium';
};
/**
 * Flatten a backend template to the frontend shape. Returns null for invalid items
 * so callers can filter instead of throwing (avoids Error Boundary on one bad template).
 */
export const flattenTemplate = (backendTemplate) => {
    try {
        if (!backendTemplate || typeof backendTemplate !== 'object') {
            return null;
        }
        if (!backendTemplate.id) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Template missing ID, skipping', backendTemplate);
            }
            return null;
        }
        const content = backendTemplate.content;
        const title = decodeQuizHtmlEntities(content?.title) || 'Untitled Quiz';
        const description = decodeQuizHtmlEntities(content?.description);
        const questions = Array.isArray(content?.questions)
            ? content.questions.map((q) => decodeContentDeep(q))
            : [];
        const flattened = {
            id: backendTemplate.id,
            type: backendTemplate.type,
            subject_id: backendTemplate.subject_id || undefined,
            classroom_id: backendTemplate.classroom_id,
            difficulty: backendTemplate.difficulty,
            created_by: backendTemplate.created_by,
            metadata: backendTemplate.metadata ?? {},
            created_at: backendTemplate.created_at,
            status: toQuizStatus(backendTemplate.status),
            title,
            description,
            questions: questions.map((q) => ({
                ...q,
                points: q?.points ?? 1,
            })),
            timer_enabled: backendTemplate.timer_enabled ?? false,
            timer_minutes: backendTemplate.timer_minutes,
            numberOfQuestions: questions.length,
            enableDiversity: false,
            updated_at: backendTemplate.updated_at ?? backendTemplate.created_at ?? new Date().toISOString(),
        };
        return flattened;
    }
    catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('Error flattening template, skipping:', error, backendTemplate);
        }
        return null;
    }
};
const toStringSafe = (val) => {
    return typeof val === 'string' ? val : (val?.text || val?.title || val?.statement || val?.description || JSON.stringify(val ?? ''));
};
/** Reference text for subjective types: worker/API may send answer, model_answer, or guidance only */
function subjectiveReferenceFromFormatted(formatted) {
    const f = formatted;
    const a = f?.answer != null ? String(f.answer).trim() : '';
    if (a)
        return a;
    const m = f?.model_answer != null ? String(f.model_answer).trim() : '';
    if (m)
        return m;
    const g = f?.guidance != null ? String(f.guidance).trim() : '';
    return g || '';
}
function skillPatchFromBackend(bq, formatted) {
    return skillTagsFromQuestion({
        skill_label: formatted?.skill_label ?? bq?.skill_label ?? bq?.question?.skill_label,
        skill_codes: formatted?.skill_codes ?? bq?.skill_codes ?? bq?.question?.skill_codes,
    });
}
function mixupPatchFromBackend(bq, formatted) {
    const out = {};
    const rawMap = formatted?.distractor_misconceptions ||
        bq?.distractor_misconceptions;
    if (rawMap && typeof rawMap === 'object' && !Array.isArray(rawMap)) {
        const map = {};
        for (const letter of ['A', 'B', 'C', 'D']) {
            const v = rawMap[letter] ?? rawMap[letter.toLowerCase()];
            if (typeof v === 'string' && v.trim())
                map[letter] = v.trim().slice(0, 128);
        }
        if (Object.keys(map).length)
            out.distractor_misconceptions = map;
    }
    const families = formatted?.misconceptions ?? bq?.misconceptions;
    if (Array.isArray(families)) {
        const list = families
            .filter((x) => typeof x === 'string' && x.trim().length > 0)
            .map((x) => x.trim().slice(0, 128))
            .slice(0, 20);
        if (list.length)
            out.misconceptions = list;
    }
    return out;
}
export const buildQuestionFromBackend = (bq) => {
    const type = (bq?.question?.type || 'multiple-choice').toLowerCase().replace(/_/g, '-');
    const formatted = bq?.question?.formatted || {};
    const qText = toStringSafe(formatted.question);
    const difficulty = mapBackendDifficultyToLegacy(bq?.metadata?.difficulty);
    const baseMeta = {
        source: 'unified-ai',
        generatedAt: bq?.metadata?.generatedAt,
        subject: bq?.metadata?.subject,
        subcategory: bq?.metadata?.subcategory,
        region: bq?.metadata?.region,
        level: bq?.metadata?.level,
        qualityScore: bq?.metadata?.qualityScore,
        qualityPassed: bq?.metadata?.qualityPassed,
        validationLevel: bq?.metadata?.validationLevel,
        explanation: formatted?.explanation || formatted?.guidance,
    };
    // The generator's diagram spec travels on `formatted.figure`. Validate once
    // here so every question shape below carries either a drawable figure or none.
    const figureResult = parseFigureSpec(formatted?.figure);
    const figurePatch = figureResult.ok ? { figure: figureResult.spec } : {};
    const skillPatch = skillPatchFromBackend(bq, formatted);
    const mixupPatch = mixupPatchFromBackend(bq, formatted);
    const isValidQuestion = quizAIService.validateQuestionTypeCompliance({
        question: qText,
        correctAnswer: formatted?.answer,
        guidance: formatted?.guidance,
        model_answer: formatted?.model_answer,
        answerA: formatted.options?.[0],
        answerB: formatted.options?.[1],
        answerC: formatted.options?.[2],
        answerD: formatted.options?.[3],
        scenario: formatted?.scenario,
    }, type);
    if (!isValidQuestion) {
        if (process.env.NODE_ENV === 'development') {
            console.warn(`Question type compliance validation failed for type: ${type}`, {
                question: qText,
                type,
                formatted,
            });
        }
        baseMeta.validationFailed = true;
        baseMeta.validationError = `Question does not meet ${type} format requirements`;
    }
    if (type === 'multiple-choice') {
        const opts = formatted.options ?? [];
        const rawAns = formatted.answer;
        // Generators emit the answer as a letter, a 1-based index, or the full
        // option text. Resolve all three — falling back to 'A' silently marks the
        // wrong option correct.
        const resolvedIndex = resolveMultipleChoiceCorrectIndex(rawAns, opts);
        const correctLetter = resolvedIndex >= 0 ? String.fromCharCode(65 + resolvedIndex) : 'A';
        return {
            id: bq.id,
            type: 'multiple_choice',
            difficulty,
            question: qText,
            answerA: opts[0] || '',
            answerB: opts[1] || '',
            answerC: opts[2] || '',
            answerD: opts[3] || '',
            correctAnswer: correctLetter,
            ...figurePatch,
            ...skillPatch,
            ...mixupPatch,
            metadata: baseMeta,
        };
    }
    if (type === 'true-false') {
        const ansRaw = String(formatted?.answer ?? '').toLowerCase();
        const tfAns = ansRaw === 'true' || ansRaw === 't' ? 'true' : 'false';
        return {
            id: bq.id,
            type: 'true_false',
            difficulty,
            question: qText,
            correctAnswer: tfAns,
            ...figurePatch,
            ...skillPatch,
            ...mixupPatch,
            metadata: baseMeta,
        };
    }
    if (type === 'short-answer') {
        return {
            id: bq.id,
            type: 'short_answer',
            difficulty,
            question: qText,
            correctAnswer: toStringSafe(formatted?.answer),
            ...figurePatch,
            ...skillPatch,
            ...mixupPatch,
            metadata: baseMeta,
        };
    }
    if (type === 'open-ended') {
        const ref = subjectiveReferenceFromFormatted(formatted);
        return {
            id: bq.id,
            type: 'open_ended',
            difficulty,
            question: qText,
            correctAnswer: ref,
            ...figurePatch,
            ...skillPatch,
            ...mixupPatch,
            metadata: baseMeta,
        };
    }
    if (type === 'scenario-based') {
        const ref = subjectiveReferenceFromFormatted(formatted);
        return {
            id: bq.id,
            type: 'scenario_based',
            difficulty,
            question: qText,
            scenario: toStringSafe(formatted?.scenario),
            correctAnswer: ref,
            guidance: toStringSafe(formatted?.guidance),
            ...figurePatch,
            ...skillPatch,
            ...mixupPatch,
            metadata: baseMeta,
        };
    }
    return {
        id: bq.id,
        type: 'multiple_choice',
        difficulty,
        question: qText,
        answerA: '',
        answerB: '',
        answerC: '',
        answerD: '',
        correctAnswer: 'A',
        ...figurePatch,
        ...skillPatch,
        ...mixupPatch,
        metadata: baseMeta,
    };
};
